<template>
  <div>
    <div class="d-flex flex-row mb-6 align-center">
      <div>
        <p class="text-2xl mb-0 primary--text font-weight-bold">
          Transcription vidéo
        </p>
        <p class="text-sm grey--text mb-0">
          Brique de transcription par défaut et brique de fallback, pour chaque
          plateforme — pour la page vidéo, le process via sources, la veille
          globale, l'URL batch et le full auto.
        </p>
      </div>
      <v-spacer></v-spacer>
      <rmr-action-button
        label="Enregistrer"
        :icon="icons.mdiContentSave"
        variant="primary"
        size="sm"
        :loading="saving"
        :disabled="!dirty || saving"
        @click="save"
      />
    </div>

    <rmr-notice
      variant="info"
      class="mb-4"
      text="La brique par défaut de la plateforme est essayée en premier. Si elle échoue, la brique de fallback de la même plateforme prend le relais et un avertissement est remonté (page vidéo, suivi des demandes). Si le fallback échoue aussi : erreur. Un fallback identique au défaut revient à ne pas avoir de fallback. Dans la page vidéo, un choix à la main est strict : pas de fallback."
    />

    <v-card shadow="none" class="elevation-1">
      <v-simple-table>
        <thead>
          <tr>
            <th>Plateforme</th>
            <th>Brique par défaut</th>
            <th>Time codes</th>
            <th>Fallback</th>
            <th>Time codes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="5" class="grey--text">Chargement…</td>
          </tr>
          <tr v-for="row in rows" v-else :key="row.platform">
            <td class="font-weight-bold">{{ row.label }}</td>
            <td class="tc-config__cell">
              <rmr-select
                :value="draft[row.platform]"
                :items="itemsFor(row.providers)"
                item-text="text"
                item-value="value"
                no-chips
                size="sm"
                @input="setChoice(row.platform, $event)"
              />
            </td>
            <td>
              <rmr-soft-badge
                v-if="chosen(row.providers, draft[row.platform])"
                v-bind="badgeFor(chosen(row.providers, draft[row.platform]))"
                size="xs"
              />
            </td>
            <td class="tc-config__cell">
              <!-- Mêmes candidates que le défaut : une brique ne peut servir
                   de fallback que sur les plateformes qu'elle gère (Gemini
                   3.8 ne lit que YouTube). -->
              <rmr-select
                :value="draft[fallbackKey(row.platform)]"
                :items="itemsFor(row.providers)"
                item-text="text"
                item-value="value"
                no-chips
                size="sm"
                @input="setChoice(fallbackKey(row.platform), $event)"
              />
            </td>
            <td>
              <rmr-soft-badge
                v-if="chosen(row.providers, draft[fallbackKey(row.platform)])"
                v-bind="
                  badgeFor(
                    chosen(row.providers, draft[fallbackKey(row.platform)])
                  )
                "
                size="xs"
              />
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref, set } from "@vue/composition-api";
import { mdiContentSave } from "@mdi/js";
import axios from "@axios";
import store from "@/store";

import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrNotice from "@/components/ui/feedback/RmrNotice.vue";
import RmrSoftBadge from "@/components/ui/feedback/RmrSoftBadge.vue";
import RmrSelect from "@/components/ui/forms/RmrSelect.vue";
import { RMR_SUCCESS, RMR_WARNING } from "@/styles/tokens";

const BASE_URL = "/aif/transcription-config/";

/** Clé du fallback d'une plateforme, côté API comme dans `draft`. */
const fallbackKey = (platform) => `fallback_${platform}`;

/** Props de la pastille « time codes » / « sans time codes » d'une brique. */
const badgeFor = (provider) => ({
  label: provider.has_timestamps ? "time codes" : "sans time codes",
  color: provider.has_timestamps ? RMR_SUCCESS : RMR_WARNING,
});

export default {
  name: "TranscriptionConfig",
  components: { RmrActionButton, RmrNotice, RmrSelect, RmrSoftBadge },
  setup() {
    const loading = ref(false);
    const saving = ref(false);
    const rows = ref([]);
    // Choix en cours d'édition (défaut et `fallback_<plateforme>`) et derniers
    // choix enregistrés : la différence des deux, c'est `dirty`.
    //
    // Les clés par plateforme n'existent qu'après le chargement : en Vue 2,
    // une clé ajoutée après coup n'est PAS réactive sans `set`. Sans lui,
    // `dirty` ne se recalculait jamais sur un changement de brique et le
    // bouton Enregistrer restait grisé (constaté en prod le 25/09/2026,
    // première fois que quelqu'un changeait un défaut par plateforme).
    const draft = reactive({});
    const saved = reactive({});

    // =>  COMPUTED
    const keys = computed(() =>
      rows.value.flatMap((row) => [row.platform, fallbackKey(row.platform)])
    );

    const dirty = computed(() =>
      keys.value.some((key) => draft[key] !== saved[key])
    );

    // =>  METHODS
    const notify = (text, color = "success") =>
      store.dispatch("app/setSnackBar", { show: true, text, color });

    const notifyError = (error, fallback) => {
      const detail = error.response?.data?.detail;
      notify(typeof detail === "string" ? detail : fallback, "error");
    };

    const applyConfig = (config) => {
      rows.value = config.platforms || [];
      rows.value.forEach((row) => {
        set(draft, row.platform, row.default_provider);
        set(saved, row.platform, row.default_provider);
        set(draft, fallbackKey(row.platform), row.fallback_provider);
        set(saved, fallbackKey(row.platform), row.fallback_provider);
      });
    };

    const itemsFor = (providers) =>
      providers.map((item) => ({ value: item.key, text: item.label }));

    const chosen = (providers, key) =>
      providers.find((item) => item.key === key) || null;

    const setChoice = (key, value) => {
      // Pas de valeur vide : une brique est obligatoire, le select ne propose
      // que des briques.
      if (value) set(draft, key, value);
    };

    const fetchData = async () => {
      loading.value = true;
      try {
        const { data } = await axios.get(BASE_URL);
        applyConfig(data);
      } catch (error) {
        notifyError(error, "Erreur lors du chargement de la configuration");
      } finally {
        loading.value = false;
      }
    };

    const save = async () => {
      saving.value = true;
      try {
        const payload = {};
        keys.value.forEach((key) => {
          if (draft[key] !== saved[key]) payload[key] = draft[key];
        });
        const { data } = await axios.put(BASE_URL, payload);
        applyConfig(data);
        notify("Configuration enregistrée");
      } catch (error) {
        notifyError(error, "Erreur lors de l'enregistrement");
      } finally {
        saving.value = false;
      }
    };

    // =>  HOOKS
    onMounted(fetchData);

    // =>  DATA
    return {
      badgeFor,
      chosen,
      dirty,
      draft,
      fallbackKey,
      icons: { mdiContentSave },
      itemsFor,
      loading,
      rows,
      save,
      saving,
      setChoice,
    };
  },
};
</script>

<style lang="scss" scoped>
.tc-config__cell {
  min-width: 280px;
}
</style>
